/**
 * Tiny error buffer that works before the Sentry SDK is loaded.
 *
 * This module is intentionally kept separate from lazySentry.ts so that
 * components like ErrorBoundary can statically import `captureException`
 * without pulling the dynamic `import('@sentry/react')` into the main chunk.
 */

type Sender = (error: unknown, context?: Record<string, unknown>) => void;

let _sender: Sender | null = null;
const _pending: { error: unknown; context?: Record<string, unknown> }[] = [];

/** Lightweight captureException -- works before the SDK is loaded */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (_sender) {
    _sender(error, context);
  } else {
    _pending.push({ error, context });
  }
}

/**
 * Connect the real Sentry captureException once the SDK is initialised.
 * Flushes any buffered errors.
 * @internal Called by lazySentry.ts -- not intended for external use.
 */
export function _connectSender(fn: Sender): void {
  _sender = fn;
  for (const { error, context } of _pending) {
    fn(error, context);
  }
  _pending.length = 0;
}
